<template>
  <div class="mybooks-container">
    <!-- Only show when in list mode -->
    <div v-if="!showCreateForm">
      <div class="page-header">
        <h2>Karya Saya</h2>
        <p class="subtitle">Kelola buku dan tulisan Anda di sini</p>
      </div>

      <!-- Create New Book Button -->
      <div class="create-book-card" @click="showCreateBookForm">
        <div class="create-book-content">
          <font-awesome-icon :icon="['fas', 'plus-circle']" class="create-icon" />
          <h3>Tulis Karya Baru</h3>
          <p>Mulai menulis buku atau cerita baru Anda</p>
        </div>
      </div>

      <!-- Books Filter and Sorting Controls -->
      <div class="book-controls">
        <div class="filter-tabs">
          <button 
            :class="['filter-tab', { active: activeFilter === 'all' }]"
            @click="setFilter('all')"
          >
            Semua <span v-if="books.length > 0" class="count">({{ books.length }})</span>
          </button>
          <button 
            :class="['filter-tab', { active: activeFilter === 'published' }]"
            @click="setFilter('published')"
          >
            Terpublikasi <span v-if="publishedBooks.length > 0" class="count">({{ publishedBooks.length }})</span>
          </button>
          <button 
            :class="['filter-tab', { active: activeFilter === 'draft' }]"
            @click="setFilter('draft')"
          >
            Draft <span v-if="draftBooks.length > 0" class="count">({{ draftBooks.length }})</span>
          </button>
        </div>

        <div class="sort-controls">
          <label for="bookSort">Urutkan:</label>
          <select id="bookSort" v-model="sortBy" @change="applySort">
            <option value="updated_date">Terakhir Diperbarui</option>
            <option value="title">Judul</option>
            <option value="read_count">Jumlah Baca</option>
            <option value="average_rating">Rating</option>
          </select>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
        <p>Memuat karya Anda...</p>
      </div>

      <!-- Empty State Message -->
      <div v-else-if="filteredBooks.length === 0" class="empty-state">
        <font-awesome-icon :icon="['fas', 'book']" class="empty-icon" />
        <h3>Belum Ada Karya</h3>
        <p v-if="activeFilter === 'all'">
          Anda belum memiliki karya. Klik "Tulis Karya Baru" untuk mulai menulis.
        </p>
        <p v-else-if="activeFilter === 'published'">
          Anda belum memiliki karya yang terpublikasi.
        </p>
        <p v-else>
          Anda belum memiliki karya dalam draft.
        </p>
        <button @click="showCreateBookForm" class="create-button">
          <font-awesome-icon :icon="['fas', 'plus']" /> Tulis Karya Baru
        </button>
      </div>

      <!-- Book List -->
      <div v-else class="books-section">
        <div class="book-grid">
          <div v-for="book in filteredBooks" :key="book.id" class="book-item">
            <div class="book-card" :class="{'draft-book': !book.is_published}">
              <!-- Status Label -->
              <div v-if="!book.is_published" class="status-label draft">Draft</div>
              <div v-else class="status-label published">Terpublikasi</div>

              <!-- Book Cover -->
              <div class="book-cover-container">
                <img :src="book.cover || defaultCover" alt="Book Cover" class="book-thumbnail" />
                <div class="book-overlay">
                  <h4 class="book-title-overlay">{{ book.title }}</h4>
                </div>
              </div>

              <!-- Book Info -->
              <div class="book-info fiction-info">
                <div class="book-rating" v-if="book.is_published">
                  <span v-for="star in 5" :key="star" class="star" :class="{'filled': star <= book.average_rating}">
                    <font-awesome-icon :icon="['fas', 'star']" />
                  </span>
                  <span class="rating-text">{{ formatRating(book.average_rating) }}</span>
                </div>
                <p class="book-meta">
                  <font-awesome-icon :icon="['fas', 'bars']" /> {{ book.chapters ? book.chapters.length : 0 }} bab |
                  <font-awesome-icon :icon="['fas', 'eye']" /> {{ book.read_count || 0 }} |
                  <font-awesome-icon :icon="['fas', 'clock']" /> {{ formatReadTime(book.estimated_read_time) }}
                </p>
              </div>

              <!-- Action Buttons -->
              <div class="book-actions">
                <button @click="editBook(book)" class="action-btn edit-btn">
                  <font-awesome-icon :icon="['fas', 'edit']" /> Edit
                </button>
                <button v-if="!book.is_published" @click="publishBook(book)" class="action-btn publish-btn">
                  <font-awesome-icon :icon="['fas', 'upload']" /> Publikasi
                </button>
                <button v-else @click="unpublishBook(book)" class="action-btn unpublish-btn">
                  <font-awesome-icon :icon="['fas', 'download']" /> Batalkan Publikasi
                </button>
                <button @click="deleteBook(book)" class="action-btn delete-btn">
                  <font-awesome-icon :icon="['fas', 'trash']" /> Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Confirmation Modal -->
      <div v-if="showConfirmModal" class="modal-overlay" @click.self="showConfirmModal = false">
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ confirmTitle }}</h3>
            <button @click="showConfirmModal = false" class="close-button">&times;</button>
          </div>
          <p>{{ confirmMessage }}</p>
          <div class="modal-actions">
            <button @click="showConfirmModal = false" class="btn-cancel">Batal</button>
            <button @click="confirmAction" class="btn-confirm" :class="confirmActionClass">
              <font-awesome-icon v-if="confirmLoading" :icon="['fas', 'spinner']" spin />
              <span v-if="!confirmLoading">{{ confirmButtonText }}</span>
              <span v-else>Memproses...</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Create Book Component -->
    <CreateBook 
      v-if="showCreateForm"
      :edit-book-id="editBookId"
      :edit-book-slug="editBookSlug"
      @cancel="showCreateForm = false"
      @book-saved="handleBookSaved"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { useHead } from '@unhead/vue';
import Swal from 'sweetalert2';
import placeholderImage from '@/assets/placeholder.svg';
import CreateBook from '@/components/CreateBook.vue'; // Import the CreateBook component

export default {
  name: 'MyBooks',
  components: {
    CreateBook
  },
  setup() {
    useHead({
      title: 'Karya Saya | HobiBaca',
      meta: [
        { name: 'description', content: 'Kelola semua buku dan tulisan Anda di HobiBaca' },
        { name: 'keywords', content: 'kelola buku, penulis, HobiBaca, menulis, publikasi' }
      ]
    });

    return { }; 
  },
  data() {
    return {
      books: [],
      loading: true,
      activeFilter: 'all',
      sortBy: 'updated_date',
      sortDirection: 'desc',
      showConfirmModal: false,
      confirmTitle: '',
      confirmMessage: '',
      confirmButtonText: '',
      confirmActionClass: '',
      confirmLoading: false,
      pendingActionBook: null,
      confirmAction: null,
      defaultCover: placeholderImage,
      
      // New properties for create/edit book
      showCreateForm: false,
      editBookId: null,
      editBookSlug: null
    };
  },
  computed: {
    publishedBooks() {
      return this.books.filter(book => book.is_published);
    },
    draftBooks() {
      return this.books.filter(book => !book.is_published);
    },
    filteredBooks() {
      // First filter by status
      let filteredList = [];
      if (this.activeFilter === 'all') {
        filteredList = [...this.books];
      } else if (this.activeFilter === 'published') {
        filteredList = [...this.publishedBooks];
      } else {
        filteredList = [...this.draftBooks];
      }
      
      // Then sort (without creating unnecessary copies)
      return this.sortBooksList(filteredList);
    }
  },
  async created() {
    await this.fetchMyBooks();
  },
  methods: {
    async fetchMyBooks() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          window.location.href = '/login';
          return;
        }

        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/books/my-books/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        // Handle both paginated and non-paginated responses
        if (response.data && response.data.results) {
          this.books = response.data.results;
        } else if (Array.isArray(response.data)) {
          this.books = response.data;
        } else {
          this.books = [];
          console.error('Unknown response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching books:', error);
        this.showToast('error', 'Gagal memuat daftar buku. Silakan coba lagi.');

        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      } finally {
        this.loading = false;
      }
    },
    
    setFilter(filter) {
      this.activeFilter = filter;
    },
    
    applySort() {
      // This is handled by the computed property
    },
    
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    },
    
    sortBooksList(bookList) {
      const direction = this.sortDirection === 'asc' ? 1 : -1;
      const field = this.sortBy;
      
      return bookList.slice().sort((a, b) => {
        if (field === 'title') {
          return direction * a.title.localeCompare(b.title);
        } else if (field === 'updated_date') {
          return direction * (new Date(b.updated_date) - new Date(a.updated_date));
        } else {
          // For numeric fields (read_count, average_rating)
          return direction * ((b[field] || 0) - (a[field] || 0));
        }
      });
    },
    
    formatRating(rating) {
      return rating ? parseFloat(rating).toFixed(1) : '0.0';
    },
    
    formatReadTime(minutes) {
      if (!minutes) return "< 1 menit";
      
      if (minutes < 60) {
        return `${minutes} menit`;
      } else {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0 
          ? `${hours} jam ${remainingMinutes} menit` 
          : `${hours} jam`;
      }
    },
    
    showCreateBookForm() {
      // Reset edit book ID/slug
      this.editBookId = null;
      this.editBookSlug = null;
      // Show create form
      this.showCreateForm = true;
    },
    
    editBook(book) {
      // Set book to edit
      this.editBookId = book.id;
      this.editBookSlug = book.slug;
      // Show create/edit form
      this.showCreateForm = true;
    },
    
    handleBookSaved() {
      // Hide create form
      this.showCreateForm = false;
      // Refresh books list
      this.fetchMyBooks();
    },
    
    publishBook(book) {
      this.pendingActionBook = book;
      this.confirmTitle = 'Publikasikan Buku';
      this.confirmMessage = `Apakah Anda yakin ingin mempublikasikan "${book.title}"? Buku yang sudah dipublikasi akan dapat dibaca oleh semua pengguna.`;
      this.confirmButtonText = 'Publikasikan';
      this.confirmActionClass = 'btn-publish';
      this.confirmAction = this.confirmPublish;
      this.showConfirmModal = true;
    },
    
    unpublishBook(book) {
      this.pendingActionBook = book;
      this.confirmTitle = 'Batalkan Publikasi';
      this.confirmMessage = `Apakah Anda yakin ingin membatalkan publikasi "${book.title}"? Buku yang tidak dipublikasi tidak akan dapat dibaca oleh pengguna lain.`;
      this.confirmButtonText = 'Batalkan Publikasi';
      this.confirmActionClass = 'btn-unpublish';
      this.confirmAction = this.confirmUnpublish;
      this.showConfirmModal = true;
    },
    
    deleteBook(book) {
      this.pendingActionBook = book;
      this.confirmTitle = 'Hapus Buku';
      this.confirmMessage = `Apakah Anda yakin ingin menghapus "${book.title}"? Tindakan ini tidak dapat dibatalkan dan semua data buku akan hilang.`;
      this.confirmButtonText = 'Hapus';
      this.confirmActionClass = 'btn-delete';
      this.confirmAction = this.confirmDelete;
      this.showConfirmModal = true;
    },
    
    async confirmPublish() {
      if (!this.pendingActionBook) return;
      
      this.confirmLoading = true;
      try {
        const token = localStorage.getItem('token');
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/api/books/${this.pendingActionBook.slug}/publish/`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Update local book data with new array
        this.books = this.books.map(book => 
          book.id === this.pendingActionBook.id 
            ? {...book, is_published: true} 
            : book
        );
        
        this.showToast('success', 'Buku berhasil dipublikasikan!');
        this.showConfirmModal = false;
      } catch (error) {
        console.error('Error publishing book:', error);
        this.showToast('error', 'Gagal mempublikasikan buku. Silakan coba lagi.');
      } finally {
        this.confirmLoading = false;
        this.pendingActionBook = null;
      }
    },
    
    async confirmUnpublish() {
      if (!this.pendingActionBook) return;
      
      this.confirmLoading = true;
      try {
        const token = localStorage.getItem('token');
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/api/books/${this.pendingActionBook.slug}/unpublish/`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Update local book data with new array
        this.books = this.books.map(book => 
          book.id === this.pendingActionBook.id 
            ? {...book, is_published: false} 
            : book
        );
        
        this.showToast('success', 'Publikasi buku berhasil dibatalkan!');
        this.showConfirmModal = false;
      } catch (error) {
        console.error('Error unpublishing book:', error);
        this.showToast('error', 'Gagal membatalkan publikasi buku. Silakan coba lagi.');
      } finally {
        this.confirmLoading = false;
        this.pendingActionBook = null;
      }
    },
    
    async confirmDelete() {
      if (!this.pendingActionBook) return;
      
      this.confirmLoading = true;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.VUE_APP_API_BASE_URL}/api/books/${this.pendingActionBook.slug}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Create new array without the deleted book
        this.books = this.books.filter(b => b.id !== this.pendingActionBook.id);
        
        this.showToast('success', 'Buku berhasil dihapus!');
        this.showConfirmModal = false;
      } catch (error) {
        console.error('Error deleting book:', error);
        this.showToast('error', 'Gagal menghapus buku. Silakan coba lagi.');
      } finally {
        this.confirmLoading = false;
        this.pendingActionBook = null;
      }
    },
    
    showToast(icon, text) {
      Swal.fire({
        icon,
        text,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    }
  }
};
</script>

<style scoped>
.mybooks-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-header h2 {
  font-size: 28px;
  color: #333;
  margin-bottom: 5px;
}

.subtitle {
  color: #666;
  font-size: 16px;
}

/* Create New Book Card */
.create-book-card {
  background: linear-gradient(145deg, #ffffff, #f0f0f0);
  border: 2px dashed #488e71;
  border-radius: 10px;
  padding: 30px;
  margin-bottom: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.create-book-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(72, 142, 113, 0.1);
  border-color: #3a7259;
}

.create-book-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #488e71;
}

.create-icon {
  font-size: 40px;
  margin-bottom: 15px;
}

.create-book-content h3 {
  font-size: 22px;
  margin-bottom: 10px;
}

.create-book-content p {
  color: #666;
}

/* Books Filter and Sorting Controls */
.book-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-tabs {
  display: flex;
  gap: 10px;
}

.filter-tab {
  padding: 8px 16px;
  border: none;
  background-color: #f5f5f5;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.filter-tab:hover {
  background-color: #e0e0e0;
}

.filter-tab.active {
  background-color: #488e71;
  color: white;
}

.count {
  font-size: 0.9em;
  opacity: 0.8;
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sort-controls label {
  color: #666;
}

.sort-controls select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: white;
}

/* Loading and Empty State */
.loading-container, .empty-state {
  text-align: center;
  padding: 50px 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-top: 20px;
}

.loading-icon, .empty-icon {
  font-size: 40px;
  color: #488e71;
  opacity: 0.7;
  margin-bottom: 15px;
}

.empty-state h3 {
  font-size: 20px;
  margin-bottom: 10px;
  color: #333;
}

.empty-state p {
  color: #666;
  margin-bottom: 20px;
  max-width: 500px;
  margin: 0 auto 20px;
}

.create-button {
  background-color: #488e71;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 15px;
}

.create-button:hover {
  background-color: #3a7259;
}

/* Book Grid */
.book-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.book-item {
  position: relative;
}

.book-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  position: relative;
  height: 100%;
}

.book-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.draft-book {
  border: 1px dashed #ccc;
  background-color: #fafafa;
}

.status-label {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: bold;
  z-index: 1;
}

.status-label.draft {
  background-color: #e0e0e0;
  color: #666;
}

.status-label.published {
  background-color: #4CAF50;
  color: white;
}

.book-cover-container {
  position: relative;
  height: 250px;
  overflow: hidden;
}

.book-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.book-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 15px 10px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
}

.book-title-overlay {
  font-size: 16px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.book-info {
  padding: 15px;
}

.book-rating {
  margin-bottom: 10px;
}

.star {
  color: #ddd;
  margin-right: 2px;
}

.star.filled {
  color: #FFD700;
}

.rating-text {
  margin-left: 5px;
  color: #666;
  font-size: 14px;
}

.book-meta {
  font-size: 13px;
  color: #666;
  margin: 0;
}

.book-actions {
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eee;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.edit-btn {
  background-color: #f0f0f0;
  color: #333;
}

.edit-btn:hover {
  background-color: #e0e0e0;
}

.publish-btn {
  background-color: #4CAF50;
  color: white;
}

.publish-btn:hover {
  background-color: #45a049;
}

.unpublish-btn {
  background-color: #FF9800;
  color: white;
}

.unpublish-btn:hover {
  background-color: #F57C00;
}

.delete-btn {
  background-color: #f44336;
  color: white;
}

.delete-btn:hover {
  background-color: #e53935;
}

/* Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 25px;
  width: 100%;
  max-width: 400px;
  position: relative; /* Ensure relative positioning for absolute children */
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
  margin-bottom: 15px;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #aaa;
  position: absolute;
  top: 10px; /* Adjusted top position */
  right: 10px; /* Adjusted right position */
  padding: 5px; /* Add padding for easier clicking */
}

.close-button:hover {
  color: #777;
}

.modal-content h3 {
  margin-top: 0;
  color: #333;
  font-size: 20px;
}

.modal-content p {
  margin-bottom: 20px;
  color: #555;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 10px 20px; /* Adjusted padding */
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px; /* Added font size */
  transition: background-color 0.2s ease; /* Added transition */
}

.btn-cancel:hover {
  background-color: #f0f0f0; /* Added hover state */
}

.btn-confirm {
  padding: 10px 20px; /* Adjusted padding */
  border: none; /* Removed border */
  background-color: #007bff; /* Example primary color */
  color: white; /* Text color */
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px; /* Added font size */
  transition: background-color 0.2s ease; /* Added transition */
}

.btn-confirm:hover {
  background-color: #0056b3; /* Darker color on hover */
}

.btn-confirm {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-publish {
  background-color: #4CAF50;
}

.btn-unpublish {
  background-color: #FF9800;
}

.btn-delete {
  background-color: #f44336;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .book-controls {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .filter-tabs {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 5px;
  }
  
  .book-grid {
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  }
  
  .book-actions {
    flex-wrap: wrap;
    gap: 5px;
  }
  
  .action-btn {
    flex: 1;
    padding: 5px;
    font-size: 12px;
    justify-content: center;
  }
}

@media (max-width: 500px) {
  .book-grid {
    grid-template-columns: 1fr;
  }
}
</style>
